import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { PortalTitle } from "../../Portal";
import { IoIosAddCircle } from "react-icons/io";
import {
  createList,
  getMyLists,
  getListById,
  updateListSettings,
  updateListDisplay,
  deleteList,
  addPerformer,
  removeMember,
  getMemberCommitments,
  getListCommitments,
  acceptRequest,
  declineRequest,
  resendInvite,
  revokeInvite,
} from "../../../api/roster";
import Button from "../../../components/Button/Button";
import ProfilePicture from "../../../components/icons/ProfilePicture";
import { enqueueSnackbar } from "notistack";
import { FiUserX, FiCheck, FiX, FiEdit2, FiTrash2, FiSearch, FiList, FiGrid } from "react-icons/fi";
import { IoArrowBack } from "react-icons/io5";
import { DateTime } from "luxon";
import axiosInstance from "../../../api/axiosInstance";
import { debounce } from "lodash";
import Loader from "../../../components/icons/Loader";
import { modal } from "../../../util/useModal";
import RosterAutomations from "./RosterAutomationsWorkspace";
import RosterCommitmentsConfirm, {
  ConfirmCard,
} from "../../../components/RosterCommitmentsConfirm/RosterCommitmentsConfirm";
import { PostedEventsWorkspace } from "../posted-events/PostedEvents";
import NotificationBadge from "../../../components/Notifications/NotificationBadge";
import {
  useEntityUnreadCount,
  useNotificationActions,
  useNotificationActivity,
} from "../../../context/NotificationsContext/NotificationsProvider";

const DETAIL_TABS = ["members", "events", "requests", "invites", "automations"];
const MEMBER_VIEW_PREF_KEY = "roster_member_view";
// switched from the dashboard menu (bookings → calendar / rosters), via ?bookingsTab=
const BOOKING_TABS = [
  {
    id: "calendar",
    label: "calendar",
  },
  {
    id: "rosters",
    label: "rosters",
  },
  {
    id: "status",
    label: "status",
  },
];

const getMemberViewPreference = () => {
  try {
    return localStorage.getItem(MEMBER_VIEW_PREF_KEY) === "cards" ? "cards" : "list";
  } catch {
    return "list";
  }
};

const getUserDisplayName = (user = {}) =>
  user.businessName ||
  [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
  user.username ||
  "member";

// ─── Root: roster list ───────────────────────────────────────────────────────

const BookingsDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentBookingTab = BOOKING_TABS.some((tab) => tab.id === searchParams.get("bookingsTab"))
    ? searchParams.get("bookingsTab")
    : "calendar";

  return (
    <div className="w-full pb-[5rem]">
      <PortalTitle />

      {/* dates (calendar) and status (list) share one workspace so "edit" from status can open the calendar editor */}
      {currentBookingTab !== "rosters" ? (
        <PostedEventsWorkspace
          scope="bookings"
          title="bookings"
          embedded
          compactFilters
          hideToolsPanel
          initialFiltersCollapsed
          inlineViewMode={currentBookingTab === "status" ? "list" : "calendar"}
          onInlineViewModeChange={(mode) =>
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("bookingsTab", mode === "list" ? "status" : "calendar");
              return next;
            })
          }
          guidanceCopy="select an existing show, or a date to edit/add a show"
        />
      ) : (
        <RosterDashboard embedded />
      )}
    </div>
  );
};

const RosterDashboard = ({ embedded = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const rosterIdFromUrl = searchParams.get("rosterId");

  const fetchRosters = async () => {
    setLoading(true);
    try {
      const data = await getMyLists();
      setRosters(data.lists);
    } catch {
      enqueueSnackbar("Failed to load rosters", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRosters(); }, []);

  const setRosterIdParam = useCallback((rosterId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (embedded) {
        next.set("tab", "bookings");
        next.set("bookingsTab", "rosters");
      }
      if (rosterId) next.set("rosterId", rosterId);
      else next.delete("rosterId");
      return next;
    }, { replace: true });
  }, [embedded, setSearchParams]);

  const openRoster = useCallback((rosterId) => {
    setSelectedId(rosterId);
    setRosterIdParam(rosterId);
  }, [setRosterIdParam]);

  const closeRoster = useCallback(() => {
    setSelectedId(null);
    setRosterIdParam(null);
  }, [setRosterIdParam]);

  useEffect(() => {
    if (loading) return;
    if (!rosterIdFromUrl) return;
    const exists = rosters.some((roster) => roster._id === rosterIdFromUrl);
    if (exists) {
      setSelectedId(rosterIdFromUrl);
      return;
    }
    enqueueSnackbar("this roster is no longer available", { variant: "info" });
    setSelectedId(null);
    setRosterIdParam(null);
  }, [loading, rosters, rosterIdFromUrl, setRosterIdParam]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreateLoading(true);
    try {
      const data = await createList(newName.trim());
      setRosters((prev) => [{ ...data.list, memberCount: 0, requestCount: 0 }, ...prev]);
      setNewName("");
      setCreating(false);
      openRoster(data.list._id);
    } catch {
      enqueueSnackbar("Failed to create roster", { variant: "error" });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async (roster) => {
    let released = { booked: 0, applied: 0, performers: 0 };
    try {
      released = await getListCommitments(roster._id);
    } catch {
      enqueueSnackbar("Failed to check roster bookings", { variant: "error" });
      return;
    }
    const releaseSummary = [
      released.booked > 0 && `reopen ${released.booked} booked ${released.booked === 1 ? "slot" : "slots"}`,
      released.applied > 0 && `withdraw ${released.applied} pending ${released.applied === 1 ? "application" : "applications"}`,
    ].filter(Boolean).join(" and ");

    const confirmed = await modal(({ proceed, cancel }) => (
      <ConfirmCard>
        <div className="font-['Primary-Medium'] text-kit-base">delete "{roster.displayName}"?</div>
        <div className="text-kit-sm text-[var(--inyc-primary-text-color-light)]">
          this will remove the roster and all its members. this action cannot be undone.
        </div>
        {releaseSummary && (
          <div className="rounded-kit-md border-[1px] border-danger-line bg-danger-soft px-kit-16 py-kit-12 text-kit-xs text-danger">
            {released.performers} {released.performers === 1 ? "performer is" : "performers are"} still on
            upcoming slots from this roster. deleting it will {releaseSummary}.
          </div>
        )}
        <div className="flex gap-kit-12">
          <button
            onClick={cancel}
            className="flex-1 py-kit-10 rounded-[var(--inyc-button-border-radius)] border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] font-['Primary-Medium'] text-kit-base hover:bg-[var(--inyc-primary)] transition-colors duration-150"
          >
            cancel
          </button>
          <button
            onClick={proceed}
            className="flex-1 py-kit-10 rounded-[var(--inyc-button-border-radius)] bg-danger-strong text-on-fill font-['Primary-Medium'] text-kit-base hover:bg-danger-strong-hover transition-colors duration-150"
          >
            delete
          </button>
        </div>
      </ConfirmCard>
    ), { raw: true });
    if (!confirmed) return;

    try {
      await deleteList(roster._id);
      setRosters((prev) => prev.filter((r) => r._id !== roster._id));
      if (selectedId === roster._id) closeRoster();
    } catch {
      enqueueSnackbar("Failed to delete roster", { variant: "error" });
    }
  };

  const onRosterUpdated = (updated) => {
    setRosters((prev) => prev.map((r) => r._id === updated._id ? { ...r, ...updated } : r));
  };

  if (selectedId) {
    return (
      <RosterDetail
        listId={selectedId}
        onBack={(updatedSummary) => {
          if (updatedSummary) onRosterUpdated(updatedSummary);
          closeRoster();
        }}
        onUpdated={onRosterUpdated}
      />
    );
  }

  return (
    <div className="w-full pb-[5rem]">
      {!embedded && <PortalTitle>rosters</PortalTitle>}
      {embedded && (
        // same style as the dates hint ("select a date to add a show"), lined up under the sub-headings
        <div className="mb-kit-12 pl-kit-16 font-['Primary-Medium'] text-kit-base lowercase text-[var(--inyc-primary-text-color)]">
          create rosters for each booking pool, then invite artists once your event slots are ready.
        </div>
      )}

      {/* Create new roster */}
      {creating ? (
        <div className="flex gap-kit-12 items-center mb-kit-24">
          <input
            autoFocus
            className="flex-1 border-[1px] border-[var(--inyc-input-border-color)] rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] px-kit-16 py-kit-10 text-kit-sm outline-none focus:border-[var(--inyc-accent)]"
            placeholder="roster name..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
              if (e.key === "Escape") { setCreating(false); setNewName(""); }
            }}
          />
          <Button onClick={handleCreate} disabled={createLoading || !newName.trim()} className="px-kit-16 py-kit-10 text-kit-sm whitespace-nowrap">
            create
          </Button>
          <button
            onClick={() => { setCreating(false); setNewName(""); }}
            className="p-kit-8 text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)]"
          >
            <FiX size={18} />
          </button>
        </div>
      ) : (
        // same pill as "new template" on the templates page; ml lines it up under the hint
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mb-kit-24 ml-kit-16 flex shrink-0 items-center justify-center gap-kit-8 cursor-pointer rounded-kit-full bg-[var(--inyc-input-background-color)] px-kit-16 py-kit-8 text-kit-xs font-['Primary-Bold'] text-[var(--inyc-primary-text-color)] transition-colors duration-200 hover:bg-[var(--inyc-primary-light)]"
        >
          <IoIosAddCircle size={16} />
          new roster
        </button>
      )}

      {loading ? (
        <div className="py-kit-48 flex justify-center text-[var(--inyc-primary-text-color-light)]">loading...</div>
      ) : rosters.length === 0 ? (
        <div className="py-kit-64 text-center text-[var(--inyc-primary-text-color-light)] text-kit-base">
          no rosters yet — create one above, or create one from the lineup section while posting an event
        </div>
      ) : (
        <div className="flex flex-col gap-kit-12">
          {rosters.map((roster) => (
            <div
              key={roster._id}
              role="button"
              tabIndex={0}
              aria-label={`open ${roster.displayName} roster`}
              onClick={() => setSelectedId(roster._id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedId(roster._id);
                }
              }}
              className="flex items-center gap-kit-12 py-kit-16 px-kit-16 rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] border-[1px] border-[var(--inyc-input-border-color)] cursor-pointer hover:border-[var(--inyc-accent)] transition-colors duration-150"
            >
              <div className="flex-1 min-w-0">
                <div className="font-['Primary-Medium'] text-kit-sm truncate">{roster.displayName}</div>
                <div className="flex items-center gap-kit-8 mt-kit-4">
                  <span className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                    {roster.memberCount} {roster.memberCount === 1 ? "member" : "members"}
                  </span>
                  <span className="text-kit-2xs px-kit-6 py-kit-2 rounded-kit-full bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]">
                    {roster.joinPolicy || (roster.type === "public" ? "open" : "closed")}
                  </span>
                  <span className="text-kit-2xs px-kit-6 py-kit-2 rounded-kit-full bg-[var(--inyc-input-background-color)] text-[var(--inyc-primary-text-color-light)]">
                    {roster.visibility || "public"}
                  </span>
                  {roster.requestCount > 0 && (
                    <span className="text-kit-2xs px-kit-6 py-kit-2 rounded-kit-full bg-[var(--inyc-accent)] text-white">
                      {roster.requestCount} pending
                    </span>
                  )}
                </div>
              </div>
              <NotificationBadge entityId={roster._id} dotOnly className="shrink-0" />
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(roster); }}
                aria-label={`delete ${roster.displayName} roster`}
                className="p-kit-6 rounded-kit-full hover:bg-[var(--inyc-secondary)] transition-colors duration-150 text-[var(--inyc-primary-text-color-light)] hover:text-danger shrink-0"
              >
                <FiTrash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const memberIdSet = (members = []) =>
  new Set(
    members.map((member) => String(member?.user?._id || "")).filter(Boolean)
  );

// ─── Detail view ─────────────────────────────────────────────────────────────

const RosterDetail = ({ listId, onBack, onUpdated }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { markEntityRead } = useNotificationActions();
  const activity = useNotificationActivity();
  const rosterUnreadCount = useEntityUnreadCount(listId);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestedTab = searchParams.get("rosterTab");
  const [activeTab, setActiveTab] = useState(
    DETAIL_TABS.includes(requestedTab) ? requestedTab : "members"
  );
  const [memberView, setMemberView] = useState(getMemberViewPreference);
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const [newMemberIds, setNewMemberIds] = useState(() => new Set());
  const listRef = useRef(list);
  const prevTabRef = useRef(activeTab);
  listRef.current = list;

  useEffect(() => {
    if (listId && markEntityRead) markEntityRead(listId);
  }, [listId, markEntityRead]);

  useEffect(() => {
    const tabFromUrl = searchParams.get("rosterTab");
    if (DETAIL_TABS.includes(tabFromUrl)) setActiveTab(tabFromUrl);
  }, [searchParams]);

  const selectDetailTab = (tab) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("rosterTab", tab);
      return next;
    }, { replace: true });
  };

  const updateMemberView = (view) => {
    setMemberView(view);
    try {
      localStorage.setItem(MEMBER_VIEW_PREF_KEY, view);
    } catch {}
  };

  const fetchList = useCallback(
    async ({ silent = false, detectNewMembers = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const data = await getListById(listId);
        const nextList = data.list;
        if (detectNewMembers && listRef.current) {
          const prevIds = memberIdSet(listRef.current.members);
          const nextIds = memberIdSet(nextList.members);
          const added = [...nextIds].filter((id) => !prevIds.has(id));
          if (added.length > 0) {
            setNewMemberIds((prev) => {
              const next = new Set(prev);
              added.forEach((id) => next.add(id));
              return next;
            });
          }
        }
        setList(nextList);
      } catch {
        if (!silent) {
          enqueueSnackbar("Failed to load roster", { variant: "error" });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [listId]
  );

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const notification = activity?.notification;
    if (!activity?.version || !notification?.target) return;
    if (notification.target.entityType !== "roster") return;
    if (String(notification.target.entityId) !== String(listId)) return;
    fetchList({ silent: true, detectNewMembers: true });
  }, [activity?.version, listId, fetchList]);

  useEffect(() => {
    if (prevTabRef.current === "members" && activeTab !== "members") {
      setNewMemberIds(new Set());
    }
    prevTabRef.current = activeTab;
  }, [activeTab]);

  const handleSettingChange = async (settings) => {
    const currentJoinPolicy = list.joinPolicy || (list.type === "public" ? "open" : "closed");
    const currentVisibility = list.visibility || "public";
    if (
      (settings.joinPolicy === undefined || currentJoinPolicy === settings.joinPolicy) &&
      (settings.visibility === undefined || currentVisibility === settings.visibility)
    ) return;
    try {
      const data = await updateListSettings(listId, settings);
      setList((prev) => ({ ...prev, ...data.list }));
      onUpdated({
        _id: listId,
        type: data.list.type,
        joinPolicy: data.list.joinPolicy,
        visibility: data.list.visibility,
      });
    } catch {
      enqueueSnackbar("Failed to update roster settings", { variant: "error" });
    }
  };

  const handleRename = async () => {
    if (!renameVal.trim() || renameVal.trim() === list.displayName) {
      setRenaming(false);
      return;
    }
    try {
      const data = await updateListDisplay(listId, renameVal.trim());
      setList((prev) => ({ ...prev, displayName: data.list.displayName }));
      onUpdated({ _id: listId, displayName: data.list.displayName });
      setRenaming(false);
    } catch {
      enqueueSnackbar("Failed to rename roster", { variant: "error" });
    }
  };

  const handleAddMember = (performer) => {
    setList((prev) => {
      const updatedMembers = [...prev.members, { user: performer, joinedAt: new Date(), addedBy: "manual" }];
      onUpdated({ _id: listId, memberCount: updatedMembers.length });
      return { ...prev, members: updatedMembers };
    });
  };

  const handleInviteAdded = (invite) => {
    setList((prev) => {
      const invites = prev.invites || [];
      const existingInvite = invites.some((item) => item.email === invite.email);
      const updatedInvites = existingInvite
        ? invites.map((item) => item.email === invite.email ? { ...item, ...invite } : item)
        : [...invites, invite];
      onUpdated({ _id: listId, inviteCount: updatedInvites.length });
      return { ...prev, invites: updatedInvites };
    });
    setActiveTab("invites");
  };

  const handleRemove = async (userId, username) => {
    let commitments = [];
    try {
      ({ commitments = [] } = await getMemberCommitments(listId, userId));
    } catch {
      enqueueSnackbar("Failed to check member bookings", { variant: "error" });
      return;
    }

    if (commitments.length > 0) {
      const confirmed = await modal(({ proceed, cancel }) => (
        <RosterCommitmentsConfirm
          title={`remove ${username} from ${list.displayName}?`}
          message="they're still on upcoming slots from this roster. removing them will"
          confirmLabel="remove"
          commitments={commitments}
          onCancel={cancel}
          onConfirm={proceed}
        />
      ), { raw: true });
      if (!confirmed) return;
    }

    try {
      await removeMember(listId, userId);
      setList((prev) => {
        const updatedMembers = prev.members.filter((m) => m?.user?._id !== userId);
        onUpdated({ _id: listId, memberCount: updatedMembers.length });
        return { ...prev, members: updatedMembers };
      });
      enqueueSnackbar(`${username} removed`, { variant: "info" });
    } catch {
      enqueueSnackbar("Failed to remove member", { variant: "error" });
    }
  };

  const handleAccept = async (userId) => {
    try {
      const data = await acceptRequest(listId, userId);
      setList((prev) => {
        const updatedMembers = [...prev.members, { user: data.member, joinedAt: new Date(), addedBy: "self" }];
        const updatedRequests = prev.pendingRequests.filter((r) => r?.user?._id !== userId);
        onUpdated({ _id: listId, memberCount: updatedMembers.length, requestCount: updatedRequests.length });
        return { ...prev, members: updatedMembers, pendingRequests: updatedRequests };
      });
      enqueueSnackbar("Request accepted", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to accept request", { variant: "error" });
    }
  };

  const handleDecline = async (userId) => {
    try {
      await declineRequest(listId, userId);
      setList((prev) => {
        const updatedRequests = prev.pendingRequests.filter((r) => r?.user?._id !== userId);
        onUpdated({ _id: listId, requestCount: updatedRequests.length });
        return { ...prev, pendingRequests: updatedRequests };
      });
      enqueueSnackbar("Request declined", { variant: "info" });
    } catch {
      enqueueSnackbar("Failed to decline request", { variant: "error" });
    }
  };

  const handleResendInvite = async (email) => {
    try {
      await resendInvite(listId, email);
      enqueueSnackbar(`Invite resent to ${email}`, { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to resend invite", { variant: "error" });
    }
  };

  const handleRevokeInvite = async (email) => {
    try {
      await revokeInvite(listId, email);
      setList((prev) => ({ ...prev, invites: prev.invites.filter((i) => i.email !== email) }));
      enqueueSnackbar("Invite cancelled", { variant: "info" });
    } catch {
      enqueueSnackbar("Failed to cancel invite", { variant: "error" });
    }
  };

  if (loading || !list) {
    return (
      <div className="w-full px-kit-24 pb-[5rem]">
        <div className="mt-kit-48 flex justify-center text-[var(--inyc-primary-text-color-light)]">loading...</div>
      </div>
    );
  }

  const safeMembers = (list.members || []).filter((member) => member?.user);
  const safeRequests = (list.pendingRequests || []).filter((request) => request?.user);
  const pendingCount = safeRequests.length;
  const inviteCount = list.invites?.length || 0;
  const membersBadgeCount =
    activeTab !== "members"
      ? Math.max(newMemberIds.size, rosterUnreadCount)
      : 0;
  const joinPolicy = list.joinPolicy || (list.type === "public" ? "open" : "closed");
  const visibility = list.visibility || "public";

  return (
    <div className="w-full px-kit-24 pb-[5rem]">
      {/* Back + title */}
      <div className="flex items-center gap-kit-12 mt-kit-32 mb-kit-24 border-b-[1px] border-[var(--inyc-input-border-color)] pb-kit-16">
        <button
          onClick={() => onBack({
            _id: listId,
            memberCount: safeMembers.length,
            requestCount: safeRequests.length,
            displayName: list.displayName,
            type: list.type,
            joinPolicy,
            visibility,
          })}
          className="text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)] shrink-0"
        >
          <IoArrowBack size={20} />
        </button>
        {renaming ? (
          <input
            autoFocus
            className="flex-1 bg-transparent border-b-[1px] border-[var(--inyc-accent)] text-kit-lg font-['Primary-Bold'] outline-none"
            value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") setRenaming(false);
            }}
          />
        ) : (
          <span className="flex-1 text-kit-lg font-['Primary-Bold'] truncate">{list.displayName}</span>
        )}
        <button
          onClick={() => { setRenameVal(list.displayName); setRenaming(true); }}
          className="flex items-center gap-kit-4 px-kit-8 py-kit-4 rounded-[var(--inyc-input-border-radius)] border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)] hover:border-[var(--inyc-accent)] transition-colors duration-150 shrink-0"
        >
          <FiEdit2 size={12} />
          <span className="text-kit-2xs font-['Primary-Medium']">rename</span>
        </button>
      </div>

      <div className="flex flex-col gap-kit-20">
        {/* Access / visibility toggles */}
        <div className="flex flex-col gap-kit-6">
          <span className="text-kit-2xs text-[var(--inyc-primary-text-color-light)] uppercase tracking-wide">join access</span>
          <div className="flex w-fit rounded-[var(--inyc-input-border-radius)] overflow-hidden border-[1px] border-[var(--inyc-input-border-color)]">
            {["closed", "open"].map((opt) => (
              <button
                key={opt}
                onClick={() => handleSettingChange({ joinPolicy: opt })}
                className={`px-kit-16 py-kit-8 text-kit-xs font-['Primary-Medium'] transition-colors duration-150 ${
                  joinPolicy === opt
                    ? "bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]"
                    : "bg-transparent text-[var(--inyc-primary-text-color-light)]"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          <span className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
            {joinPolicy === "closed"
              ? "closed — performers need to request and be accepted before joining"
              : "open — performers join instantly without approval"}
          </span>
        </div>

        <div className="flex flex-col gap-kit-6">
          <span className="text-kit-2xs text-[var(--inyc-primary-text-color-light)] uppercase tracking-wide">browse visibility</span>
          <div className="flex w-fit rounded-[var(--inyc-input-border-radius)] overflow-hidden border-[1px] border-[var(--inyc-input-border-color)]">
            {["public", "private"].map((opt) => (
              <button
                key={opt}
                onClick={() => handleSettingChange({ visibility: opt })}
                className={`px-kit-16 py-kit-8 text-kit-xs font-['Primary-Medium'] transition-colors duration-150 ${
                  visibility === opt
                    ? "bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]"
                    : "bg-transparent text-[var(--inyc-primary-text-color-light)]"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          <span className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
            {visibility === "private"
              ? "private — hidden from roster browse"
              : "public — visible on roster browse"}
          </span>
        </div>

        {/* Performer search */}
        <PerformerSearchAdd
          listId={listId}
          existingMembers={list.members}
          existingInvites={list.invites}
          onAdded={handleAddMember}
          onInvited={handleInviteAdded}
        />

        {/* Members / Requests tabs */}
        <div className="flex gap-kit-8 border-b-[1px] border-[var(--inyc-input-border-color)]">
          {DETAIL_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => selectDetailTab(tab)}
              className={`pb-kit-10 px-kit-8 text-kit-sm font-['Primary-Medium'] border-b-[2px] transition-colors duration-150 ${
                activeTab === tab
                  ? "border-[var(--inyc-accent)] text-[var(--inyc-primary-text-color)]"
                  : "border-transparent text-[var(--inyc-primary-text-color-light)]"
              }`}
            >
              {tab}
              {tab === "members" && membersBadgeCount > 0 && (
                <span className="ml-kit-6 bg-badge text-on-fill text-kit-2xs rounded-kit-full px-kit-6 py-kit-2">
                  {membersBadgeCount}
                </span>
              )}
              {tab === "requests" && pendingCount > 0 && (
                <span className="ml-kit-6 bg-[var(--inyc-accent)] text-white text-kit-2xs rounded-kit-full px-kit-6 py-kit-2">
                  {pendingCount}
                </span>
              )}
              {tab === "invites" && inviteCount > 0 && (
                <span className="ml-kit-6 bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)] text-kit-2xs rounded-kit-full px-kit-6 py-kit-2">
                  {inviteCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "events" && (
          <PostedEventsWorkspace
            scope="roster"
            rosterId={listId}
            rosterName={list.displayName}
            embedded
          />
        )}

        {activeTab === "members" && (
          <div className="flex flex-col gap-kit-12">
            <div className="flex items-center justify-between gap-kit-16">
              <div className="text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                {safeMembers.length} {safeMembers.length === 1 ? "member" : "members"}
              </div>
              <div className="flex rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-4">
                {[
                  { id: "list", label: "list view", Icon: FiList },
                  { id: "cards", label: "card view", Icon: FiGrid },
                ].map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    aria-label={label}
                    onClick={() => updateMemberView(id)}
                    className={`flex h-[2rem] w-[2rem] items-center justify-center rounded-kit-full transition-colors duration-150 ${
                      memberView === id
                        ? "bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]"
                        : "text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)]"
                    }`}
                  >
                    <Icon size={15} />
                  </button>
                ))}
              </div>
            </div>
            {safeMembers.length === 0 ? (
              <div className="py-kit-32 text-center text-[var(--inyc-primary-text-color-light)] text-kit-sm">
                no performers yet — search above to add
              </div>
            ) : memberView === "cards" ? (
              <div className="grid gap-kit-16 sm:grid-cols-2 xl:grid-cols-3">
                {safeMembers.map((m) => (
                  <MemberCard
                    key={m.user._id}
                    user={m.user}
                    joinedAt={m.joinedAt}
                    isNew={newMemberIds.has(String(m.user._id))}
                    onRemove={() => handleRemove(m.user._id, m.user.username)}
                  />
                ))}
              </div>
            ) : (
              safeMembers.map((m) => (
                <MemberRow
                  key={m.user._id}
                  user={m.user}
                  joinedAt={m.joinedAt}
                  isNew={newMemberIds.has(String(m.user._id))}
                  onRemove={() => handleRemove(m.user._id, m.user.username)}
                />
              ))
            )}
          </div>
        )}

        {activeTab === "requests" && (
          <div className="flex flex-col gap-kit-12">
            {safeRequests.length === 0 ? (
              <div className="py-kit-32 text-center text-[var(--inyc-primary-text-color-light)] text-kit-sm">
                no pending requests
              </div>
            ) : (
              safeRequests.map((r) => (
                <RequestRow
                  key={r.user._id}
                  user={r.user}
                  requestedAt={r.requestedAt}
                  note={r.note}
                  onAccept={() => handleAccept(r.user._id)}
                  onDecline={() => handleDecline(r.user._id)}
                />
              ))
            )}
          </div>
        )}

        {activeTab === "invites" && (
          <div className="flex flex-col gap-kit-12">
            {(!list.invites || list.invites.length === 0) ? (
              <div className="py-kit-32 text-center text-[var(--inyc-primary-text-color-light)] text-kit-sm">
                no pending email invites
              </div>
            ) : (
              list.invites.map((inv) => (
                <InviteRow
                  key={inv.email}
                  email={inv.email}
                  invitedAt={inv.invitedAt}
                  onResend={() => handleResendInvite(inv.email)}
                  onRevoke={() => handleRevokeInvite(inv.email)}
                />
              ))
            )}
          </div>
        )}

        {activeTab === "automations" && (
          <RosterAutomations listId={listId} />
        )}
      </div>
    </div>
  );
};

// ─── Performer search + add ───────────────────────────────────────────────────

const PerformerSearchAdd = ({ listId, existingMembers, existingInvites, onAdded, onInvited }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const [addingEmail, setAddingEmail] = useState(false);
  const wrapperRef = useRef(null);

  const excludeRef = useRef({ ids: [], emails: [] });
  excludeRef.current = {
    ids: (existingMembers || [])
      .map((m) => m?.user?._id || m?.user || m)
      .filter(Boolean)
      .map(String),
    emails: (existingInvites || [])
      .map((invite) => invite.email?.toLowerCase())
      .filter(Boolean),
  };

  const debouncedSearch = useRef(
    debounce(async (q) => {
      if (!q || q.trim().length < 2) { setResults([]); setShowDropdown(false); return; }
      setSearching(true);
      try {
        const { data } = await axiosInstance.get("/events/search", {
          params: { search: q, type: "account", accountType: "artist", size: 10 },
        });
        const { ids, emails } = excludeRef.current;
        const filtered = (data.data || []).filter((u) => {
          if (ids.includes(String(u._id))) return false;
          if (u.email && emails.includes(u.email.toLowerCase())) return false;
          return true;
        });
        setResults(filtered);
        setShowDropdown(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300)
  ).current;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  const moveQueryToEmailInvite = () => {
    const email = query.trim();
    setEmailInput(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "");
    setShowEmailFallback(true);
    setShowDropdown(false);
    setQuery("");
    setResults([]);
  };

  const handleSelect = async (user) => {
    setQuery("");
    setResults([]);
    setShowDropdown(false);
    try {
      const data = await addPerformer(listId, { userId: user._id });
      if (data.invited) {
        onInvited?.({
          email: data.email || user.email,
          invitedAt: data.invite?.invitedAt || new Date().toISOString(),
          token: data.invite?.token,
        });
        enqueueSnackbar("invite sent", { variant: "success" });
      } else {
        onAdded(data.member);
        enqueueSnackbar(`${data.member.username} added`, { variant: "success" });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to add performer";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const handleEmailAdd = async () => {
    if (!emailInput.trim()) return;
    setAddingEmail(true);
    try {
      const data = await addPerformer(listId, { identifier: emailInput.trim() });
      if (data.invited) {
        onInvited?.({
          email: data.email,
          invitedAt: data.invite?.invitedAt || new Date().toISOString(),
          token: data.invite?.token,
        });
        enqueueSnackbar(`Invite sent to ${data.email}`, { variant: "success" });
      }
      else { onAdded(data.member); enqueueSnackbar(`${data.member.username} added`, { variant: "success" }); }
      setEmailInput("");
      setShowEmailFallback(false);
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setAddingEmail(false);
    }
  };

  const getName = (u) => {
    if ((u.accountType === "business" || u.accountType === "artist") && u.businessName) return u.businessName;
    return u.username || `${u.firstName || ""} ${u.lastName || ""}`.trim();
  };

  return (
    <div className="flex flex-col gap-kit-8">
      {/* Search input + dropdown */}
      <div ref={wrapperRef} className="relative">
        <div className="flex items-center gap-kit-8 border-[1px] border-[var(--inyc-input-border-color)] rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] px-kit-16 py-kit-8">
          <FiSearch size={14} className="text-[var(--inyc-primary-text-color-light)] shrink-0" />
          <input
            className="flex-1 bg-transparent text-kit-sm outline-none"
            placeholder="search artists to add..."
            value={query}
            onChange={handleQueryChange}
            onFocus={() => results.length > 0 && setShowDropdown(true)}
          />
          {searching && <Loader height={14} />}
        </div>

        {showDropdown && (
          <div className="absolute top-[calc(100%+0.35rem)] left-0 right-0 bg-[var(--inyc-primary-light)] border-[1px] border-[var(--inyc-input-border-color)] rounded-[var(--inyc-input-border-radius)] shadow-lg z-[100] max-h-[260px] overflow-y-auto">
            {results.length === 0 ? (
              <div className="py-kit-16 text-center text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                no results
                {!showEmailFallback && (
                  <button
                    onClick={moveQueryToEmailInvite}
                    className="block mx-auto mt-kit-6 text-kit-xs text-[var(--inyc-accent)] underline"
                  >
                    invite artist by email instead
                  </button>
                )}
              </div>
            ) : (
              results.map((u) => (
                <button
                  type="button"
                  key={u._id}
                  onClick={() => handleSelect(u)}
                  className="flex w-full items-center gap-kit-10 px-kit-16 py-kit-10 text-left cursor-pointer hover:bg-[var(--inyc-primary)] transition-colors duration-100 border-b-[1px] border-[var(--inyc-input-border-color)] last:border-0"
                >
                  <ProfilePicture
                    username={u.username}
                    profilePicture={u.profilePicture}
                    style={{ width: "2rem", height: "2rem", fontSize: "var(--inyc-text-2xs)", flexShrink: 0 }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-kit-sm font-['Primary-Medium'] truncate">{getName(u)}</div>
                    <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">{u.accountType}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Email fallback */}
      {showEmailFallback && (
        <div className="flex gap-kit-10 items-center mt-kit-4">
          <input
            className="flex-1 border-[1px] border-[var(--inyc-input-border-color)] rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] px-kit-16 py-kit-8 text-kit-sm outline-none focus:border-[var(--inyc-accent)]"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="email"
            placeholder="email address..."
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleEmailAdd()}
          />
          <button
            onClick={handleEmailAdd}
            disabled={addingEmail || !emailInput.trim()}
            className="px-kit-16 py-kit-8 text-kit-xs font-['Primary-Medium'] bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)] rounded-[var(--inyc-input-border-radius)] whitespace-nowrap disabled:opacity-50"
          >
            invite
          </button>
          <button
            onClick={() => setShowEmailFallback(false)}
            aria-label="close email invite"
            className="text-[var(--inyc-primary-text-color-light)]"
          >
            <FiX size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const MemberRow = ({ user, joinedAt, isNew = false, onRemove }) => (
  <div className="flex items-center gap-kit-12 py-kit-12 px-kit-12 rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] border-[1px] border-[var(--inyc-input-border-color)]">
    <ProfilePicture
      username={user.username || getUserDisplayName(user)}
      profilePicture={user.profilePicture}
      style={{ width: "2.4rem", height: "2.4rem", fontSize: "var(--inyc-text-2xs)" }}
    />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-kit-6 font-['Primary-Medium'] text-kit-sm truncate">
        <span className="truncate">{getUserDisplayName(user)}</span>
        {isNew && (
          <span className="shrink-0 rounded-kit-full bg-[var(--inyc-accent)]/15 px-kit-8 py-kit-2 text-kit-2xs font-['Primary-Medium'] text-[var(--inyc-accent)]">
            new
          </span>
        )}
      </div>
      {user.username && getUserDisplayName(user) !== user.username && (
        <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)] truncate">@{user.username}</div>
      )}
      {joinedAt && (
        <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
          joined {DateTime.fromISO(joinedAt).toRelative()}
        </div>
      )}
    </div>
    <button
      onClick={onRemove}
      aria-label={`remove ${user.username} from roster`}
      className="p-kit-6 rounded-kit-full hover:bg-[var(--inyc-secondary)] transition-colors duration-150 text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)]"
    >
      <FiUserX size={16} />
    </button>
  </div>
);

const MemberCard = ({ user, joinedAt, isNew = false, onRemove }) => (
  <div className="flex min-h-[22rem] flex-col overflow-hidden rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)]">
    <div className="relative aspect-[4/3] w-full bg-[var(--inyc-input-background-color)]">
      <ProfilePicture
        username={user.username || getUserDisplayName(user)}
        profilePicture={user.profilePicture}
        className="!rounded-none !border-0 !shadow-none"
        style={{ width: "100%", height: "100%", fontSize: "var(--inyc-text-lg)", flexShrink: 0 }}
      />
      <button
        onClick={onRemove}
        aria-label={`remove ${user.username || getUserDisplayName(user)} from roster`}
        className="absolute right-[0.55rem] top-[0.55rem] rounded-kit-full bg-[var(--inyc-primary)]/80 p-kit-8 text-[var(--inyc-primary-text-color-light)] opacity-80 shadow-sm transition-colors duration-150 hover:bg-[var(--inyc-secondary)] hover:text-[var(--inyc-primary-text-color)] hover:opacity-100"
      >
        <FiUserX size={15} />
      </button>
    </div>

    <div className="mt-kit-16 min-w-0 px-kit-16">
      <div className="flex items-center gap-kit-6">
        <div className="line-clamp-1 font-['Primary-Bold'] text-kit-base text-[var(--inyc-primary-text-color)]">
          {getUserDisplayName(user)}
        </div>
        {isNew && (
          <span className="shrink-0 rounded-kit-full bg-[var(--inyc-accent)]/15 px-kit-8 py-kit-2 text-kit-2xs font-['Primary-Medium'] text-[var(--inyc-accent)]">
            new
          </span>
        )}
      </div>
      <div className="mt-kit-2 flex flex-wrap items-center gap-kit-8 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
        {user.username && <span>@{user.username}</span>}
        {user.accountType && (
          <span className="rounded-kit-full bg-[var(--inyc-input-background-color)] px-kit-8 py-kit-2">
            {user.accountType}
          </span>
        )}
      </div>
      {joinedAt && (
        <div className="mt-kit-8 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
          joined {DateTime.fromISO(joinedAt).toRelative()}
        </div>
      )}
    </div>

    <div className="mt-kit-16 flex flex-col gap-kit-8 px-kit-16 text-kit-xs">
      {user.email && (
        <a
          href={`mailto:${user.email}`}
          className="truncate text-[var(--inyc-primary-text-color)] hover:text-[var(--inyc-accent)]"
        >
          {user.email}
        </a>
      )}
      {user.rosterPhone && (
        <a
          href={`tel:${user.rosterPhone}`}
          className="truncate text-[var(--inyc-primary-text-color)] hover:text-[var(--inyc-accent)]"
        >
          {user.rosterPhone}
        </a>
      )}
    </div>

    <div className="mx-kit-16 mb-kit-16 mt-kit-16 flex-1 rounded-kit-md bg-[var(--inyc-primary)] px-kit-12 py-kit-12 text-kit-xs leading-[1.45] text-[var(--inyc-primary-text-color-light)]">
      {user.bio ? <span className="line-clamp-4">{user.bio}</span> : "no bio yet"}
    </div>
  </div>
);

const RequestRow = ({ user, requestedAt, note, onAccept, onDecline }) => (
  <div className="flex items-start gap-kit-12 py-kit-12 px-kit-12 rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] border-[1px] border-[var(--inyc-input-border-color)]">
    <ProfilePicture
      username={user.username}
      profilePicture={user.profilePicture}
      style={{ width: "2.4rem", height: "2.4rem", fontSize: "var(--inyc-text-2xs)", flexShrink: 0 }}
    />
    <div className="flex-1 min-w-0">
      <div className="font-['Primary-Medium'] text-kit-sm truncate">{user.username}</div>
      {requestedAt && (
        <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
          {DateTime.fromISO(requestedAt).toRelative()}
        </div>
      )}
      {note && (
        <div className="mt-kit-6 text-kit-xs text-[var(--inyc-primary-text-color-light)] italic line-clamp-2">
          "{note}"
        </div>
      )}
    </div>
    <div className="flex gap-kit-8 shrink-0">
      <button
        onClick={onAccept}
        aria-label={`accept ${user.username}`}
        className="p-kit-6 rounded-kit-full bg-[var(--inyc-accent)] text-white hover:opacity-80 transition-opacity duration-150"
      >
        <FiCheck size={15} />
      </button>
      <button
        onClick={onDecline}
        aria-label={`decline ${user.username}`}
        className="p-kit-6 rounded-kit-full hover:bg-[var(--inyc-secondary)] transition-colors duration-150 text-[var(--inyc-primary-text-color-light)]"
      >
        <FiX size={15} />
      </button>
    </div>
  </div>
);

const InviteRow = ({ email, invitedAt, onResend, onRevoke }) => (
  <div className="flex items-center gap-kit-12 py-kit-12 px-kit-12 rounded-[var(--inyc-input-border-radius)] bg-[var(--inyc-primary-light)] border-[1px] border-[var(--inyc-input-border-color)]">
    <div
      className="shrink-0 w-[2.4rem] h-[2.4rem] rounded-kit-full bg-[var(--inyc-secondary)] flex items-center justify-center text-kit-2xs font-['Primary-Medium'] text-[var(--inyc-secondary-text-color)] uppercase"
    >
      {email[0]}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-['Primary-Medium'] text-kit-sm truncate">{email}</div>
      {invitedAt && (
        <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
          invited {DateTime.fromISO(invitedAt).toRelative()}
        </div>
      )}
    </div>
    <div className="flex gap-kit-8 shrink-0">
      <button
        onClick={onResend}
        className="px-kit-10 py-kit-4 text-kit-2xs font-['Primary-Medium'] rounded-[var(--inyc-input-border-radius)] border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] hover:border-[var(--inyc-accent)] hover:text-[var(--inyc-primary-text-color)] transition-colors duration-150"
      >
        resend
      </button>
      <button
        onClick={onRevoke}
        aria-label={`revoke invite for ${email}`}
        className="p-kit-6 rounded-kit-full hover:bg-[var(--inyc-secondary)] transition-colors duration-150 text-[var(--inyc-primary-text-color-light)]"
      >
        <FiX size={15} />
      </button>
    </div>
  </div>
);

export default BookingsDashboard;
